// GmailAuth.jsx - Improved toast version
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Snackbar, Alert } from '@mui/material';

function GmailAuth({ onAuthComplete, postId, sourceCall,sujet }) {
  const [authStatus, setAuthStatus] = useState('requesting');
  const [sessionId, setSessionId] = useState(null);
  const [popupRef, setPopupRef] = useState(null);
  const [pollInterval, setPollInterval] = useState(null);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState('info');
  const [showToast, setShowToast] = useState(false);
  
  // Use these default values automatically
//   const subject = 'Internship_2024';
//   const description = 'test';
  
  const apiUrl = process.env.REACT_APP_API_URL;

  // Start authentication automatically when component mounts
  useEffect(() => {
    startAuth();
    
    // Clean up any poll interval
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, []);
  
  const startAuth = async () => {
    setAuthStatus('requesting');
    showToastMessage('Connecting to Gmail...', 'info');
    
    try {
      const response = await axios.get(`${apiUrl}ai/start-gmail-auth/`);  
      const { auth_url, session_id } = response.data;
      
      setSessionId(session_id);
      
      // Open the auth URL in a popup window
      const popup = window.open(auth_url, 'GmailAuth', 'width=600,height=600');
      setPopupRef(popup);
      
      showToastMessage('Please authenticate in the popup window', 'info');
      
      // Poll to check if authentication is complete
      const interval = setInterval(() => {
        if (popup.closed) {
          clearInterval(interval);
          setPollInterval(null);
          checkAuthStatus(session_id);
        }
      }, 1000);
      
      setPollInterval(interval);
      setAuthStatus('authenticating');
    } catch (error) {
      console.error('Auth error:', error);
      setAuthStatus('error');
      showToastMessage('Authentication failed. Please try again.', 'error');
      if (onAuthComplete) {
        onAuthComplete(null, { status: 'error', message: 'Authentication failed' });
      }
    }
  };
  
  const checkAuthStatus = async (sid) => {
    try {
      const response = await axios.post(`${apiUrl}ai/check-auth-status/`, { session_id: sid });
      
      if (response.data.authenticated) {
        setAuthStatus('authenticated');
        showToastMessage('Gmail connected! Processing emails...', 'success');
        // Automatically start processing emails after auth is complete
        processGmail(sid);
      } else {
        setAuthStatus('failed');
        showToastMessage('Authentication failed. Please try again.', 'error');
        if (onAuthComplete) {
          onAuthComplete(null, { status: 'failed', message: 'Authentication failed' });
        }
      }
    } catch (error) {
      setAuthStatus('failed');
      showToastMessage('Authentication failed. Please try again.', 'error');
      if (onAuthComplete) {
        onAuthComplete(null, { status: 'error', message: error.message });
      }
    }
  };
  
  const processGmail = async (sid) => {
    setAuthStatus('processing');
    
    try {
      const response = await axios.post(`${apiUrl}ai/process-gmail/`, {
        session_id: sid,
        // subject: subject,
        post_id: parseInt(postId ),
        source_call:sourceCall,
        sujet:sujet,
        // description: description
      });
      
      setAuthStatus('complete');
      
      // Handle different result statuses
      if (response.data.status === 'success') {
        const fileCount = response.data.processed_files?.length || 0;
        showToastMessage(`Successfully processed ${fileCount} file(s)`, 'success');
      } else if (response.data.status === 'no_emails_found') {
        showToastMessage('No emails found matching the criteria', 'warning');
      } else if (response.data.status === 'no_attachments_found') {
        showToastMessage('No attachments found in the matching emails', 'warning');
      } else {
        showToastMessage('Error: ' + (response.data.message || 'Unknown error'), 'error');
      }
      
      // Notify parent component of completion
      if (onAuthComplete) {
        onAuthComplete(sid, response.data);
      }
    } catch (error) {
      console.error('Processing error:', error);
      setAuthStatus('error');
      showToastMessage('Error processing emails: ' + error.message, 'error');
      if (onAuthComplete) {
        onAuthComplete(null, { status: 'error', message: error.message });
      }
    }
  };
  
  const showToastMessage = (message, severity) => {
    setToastMessage(message);
    setToastSeverity(severity);
    setShowToast(true);
  };
  
  const handleCloseToast = () => {
    setShowToast(false);
  };
  
  return (
    <Snackbar
      open={showToast}
      autoHideDuration={6000}
      onClose={handleCloseToast}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }} // Position in top-right
      sx={{ 
        '& .MuiAlert-root': { 
          minWidth: '300px', // Wider toast for better readability
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)' // Add shadow for visibility
        }
      }}
    >
      <Alert 
        onClose={handleCloseToast} 
        severity={toastSeverity} 
        variant="filled" // Make it more visible with filled background
        sx={{ width: '100%' }}
      >
        {toastMessage}
      </Alert>
    </Snackbar>
  );
}

export default GmailAuth;